/*
 * This file is part of the Atlanterhavskomiteen Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import useNav from 'effects/useNav';
import Icon from 'components/Icons/Content/Content';
import Menu from './Menu/Menu';
import './Nav.scss';

/**
 * This is the Menu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */

const Nav = forwardRef(({ labels, ...props }, ref) => {
  const navRef = useRef(null);
  const [buttonNode, menuNode, open, setOpen] = useNav('nav');

  useImperativeHandle(ref, () => ({
    node: navRef.current,
    open: () => {
      setOpen(true);
    },
    close: () => {
      setOpen(false);
    },
  }));

  useEffect(() => {
    if (open) {
      document.body.classList.add('menu-open');
    }

    return function cleanup() {
      document.body.classList.remove('menu-open');
    };
  }, [open]);

  return (
    <>
      <nav ref={navRef} className="nav">
        <button
          ref={buttonNode}
          id="menubutton"
          className="toggle"
          aria-label={open ? labels.close : labels.open}
          aria-haspopup="true"
          aria-controls="menu"
          aria-expanded={open ? 'true' : 'false'}
        >
          <span>
            <Icon title={labels.menu} />
            <span className="text">{labels.menu}</span>
          </span>
        </button>
      </nav>
      <Menu
        ref={menuNode}
        id="menu"
        controller="menubutton"
        {...props}
        active={open}
        labels={labels}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
});

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Nav.propTypes = {
  labels: PropTypes.shape({
    menu: PropTypes.string.isRequired,
    open: PropTypes.string.isRequired,
    close: PropTypes.string.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Nav.defaultProps = {
  labels: {
    menu: 'Menu',
    open: 'Open',
    close: 'Close',
  },
  onClick: () => {},
};

export default Nav;
